import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ScannedActionsSubject, Store } from '@ngrx/store';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { AppUtilityService } from '../../services/app-utility.service';
import { ReauthOutcome, ReauthService } from '../../services/reauth.service';
import { SaveDialogData } from '../../models/dialog.model';
import { CommitRequest} from '../../models/study.model';
import { selectAppState } from '../../../app/state/selectors';
import { commitStudy, saveStudyError, saveStudySuccess } from '../../../app/state/actions';
import { getLatestAuditTrailComment } from '../../utils/audit-comment.util';
/**
 * CommitStudyDialogComponent
 * This component is responsible for rendering a dialog that allows users to
 * commit (save) a study. It gathers the necessary details, triggers the commit action,
 * and updates the UI based on the result of the commit process.
 */
@Component({
  selector: 'app-commit-study-dialog',
  templateUrl: './commit-dialog.component.html',
  styleUrl: './commit-dialog.component.scss'
})

export class CommitStudyDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<CommitStudyDialogComponent>);
  readonly data = inject<SaveDialogData>(MAT_DIALOG_DATA);
  readonly selectedStudy = this.data.study;

  appState$ = this.store.select(selectAppState);
  private destroy$ = new Subject<void>();
  studyName = this.selectedStudy ? this.selectedStudy.studyName : '';
  currentDateFormatted!: string;
  errorMessage!: string;
  isProcessing = false;
  panelName: string = '';
  panelPhase: string = '';
  testSpecPhase: string = '';
  operator: string = '';
  calculationStatus: string = '';
  currentComments = '';
  lastSavedComment = '';
  lastAuditComment = '';
  commentInput = '';
  commentError = '';
  private commentEdited = false;

  /**
   * 
   * @param store Injecting store for state management.
   * @param appUtilityService Service for API calls and utility functions.
   * @param actions
   * @param reauthService Forces an Active Directory re-authentication before the commit.
   */
  constructor(
    private store: Store,
    private appUtilityService: AppUtilityService,
    private actions: ScannedActionsSubject,
    private reauthService: ReauthService,
  ) { }
  /**
     * ngOnInit lifecycle hook to initialize the component.
     * - Formats the current date for display.
     * - Subscribes to app state changes to update the component with panel and operator information.
     */
  ngOnInit(): void {
    this.currentDateFormatted = this.appUtilityService.formatDateTime(new Date());
    this.appState$.pipe(
      filter(appState => !!appState),
      takeUntil(this.destroy$)
    ).subscribe(appState => {
      this.panelName = appState.panelName;
      this.panelPhase = appState.selectedStudy.runType;
      this.testSpecPhase = appState.selectedStudy.testSpec;
      this.operator = appState.loggedInUser;
      this.calculationStatus = appState.calculationStatus;
      this.currentComments = appState.selectedStudy.comments ?? '';
      this.lastSavedComment = appState.lastSavedComment ?? '';
      this.lastAuditComment = getLatestAuditTrailComment(appState.selectedStudy.auditTrail ?? []);
      if (!this.commentEdited) {
        this.commentInput = this.currentComments;
      }
    });
  }

  /**
 * commitStudy - Requires the operator to re-authenticate, then commits the study.
 * - Validates the comments first, synchronously, so the popup below is still opened
 *   from within the click handler. Doing async work first would let the browser's
 *   pop-up blocker cancel the Active Directory window.
 * - Only dispatches the commit once Active Directory confirms the operator's
 *   identity; any other outcome leaves the dialog open with an explanation so they
 *   can retry or cancel.
 */
  commitStudy() {
    if (!this.isProcessing) {
      if (!this.validateComments()) {
        return;
      }

      this.isProcessing = true;
      this.errorMessage = '';

      this.reauthService.reauthenticate().pipe(
        take(1),
        takeUntil(this.destroy$)
      ).subscribe(outcome => {
        if (outcome.status === 'success') {
          this.dispatchCommit(outcome.reauthToken);
          return;
        }
        this.isProcessing = false;
        this.errorMessage = this.reauthErrorMessage(outcome);
      });
    }
  }

  /**
   * Dispatches the commit and closes the dialog. Reached only after a verified
   * re-authentication, whose ID token travels with the request so the server can
   * confirm it independently.
   */
  private dispatchCommit(reauthToken: string) {
    this.actions.pipe(
      filter(action => action.type === saveStudySuccess.type
        || action.type === saveStudyError.type
      ),
      take(1)
    ).subscribe(() => {
      this.isProcessing = false;
    });

    const commitNotes = this.commentInput.trim();
    const commitStudyRequest = new CommitRequest(this.selectedStudy.studyName, commitNotes, reauthToken)

    this.store.dispatch(commitStudy({
      commitStudyRequest: commitStudyRequest
    }));

    this.dialogRef.close();
  }

  /** Explains why a re-authentication did not authorise the commit. */
  private reauthErrorMessage(outcome: Exclude<ReauthOutcome, { status: 'success' }>): string {
    switch (outcome.status) {
      case 'cancelled':
        return 'Commit cancelled. Signing in to Active Directory is required to commit a study.';
      case 'identity-mismatch':
        return `Signed in as ${outcome.actual}. This study must be committed by ${outcome.expected}.`;
      case 'stale-auth':
        return 'Active Directory did not re-authenticate. Commit cancelled.';
      default:
        return outcome.message;
    }
  }

  handleCommentInputChange(value: string): void {
    this.commentEdited = true;
    this.commentInput = value;
    this.commentError = '';
  }

  private normalizeComment(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private validateComments(): boolean {
    const normalizedNew = this.normalizeComment(this.commentInput);
    const normalizedPrev = this.normalizeComment(this.lastAuditComment || this.lastSavedComment);

    if (!normalizedNew.length) {
      this.commentError = 'Please enter comments before committing this study.';
      return false;
    }

    if (normalizedNew === normalizedPrev) {
      this.commentError = 'Comment must differ from the previous saved comment.';
      return false;
    }

    this.commentError = '';
    return true;
  }
}
